'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import type { HeroCarouselSlide } from '../../types/banner';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import styles from './HeroCarousel.module.css';

interface HeroCarouselProps {
  slides: HeroCarouselSlide[];
  autoPlayInterval?: number;
  showDots?: boolean;
}

export const HeroCarousel: React.FC<HeroCarouselProps> = ({
  slides,
  autoPlayInterval = 7500,
  showDots = true,
}) => {
  const { localizeFullPath } = useLocalizedPath();
  const t = useTranslations('homepage.hero');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReducedMotion(mediaQuery.matches);
    onChange();
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);
  // Chromium does not reliably re-run <picture> source selection when the
  // viewport crosses the breakpoint AFTER load (spec makes it optional) - the
  // initially chosen variant sticks, so shrinking a desktop window / DevTools
  // responsive mode kept showing the desktop image. Bumping this key on each
  // 640px media-query flip remounts the <img>, forcing a fresh selection.
  // No change event fires on initial load, so the LCP path is untouched.
  const [viewportGeneration, setViewportGeneration] = useState(0);
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 640px)');
    const onChange = () => setViewportGeneration((generation) => generation + 1);
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const isPointerDown = useRef<boolean>(false);
  const hasDragged = useRef<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const totalSlides = slides.length;

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex((index + totalSlides) % totalSlides);
  }, [totalSlides]);

  const goToNext = useCallback(() => {
    goToSlide(currentIndex + 1);
  }, [currentIndex, goToSlide]);

  const goToPrev = useCallback(() => {
    goToSlide(currentIndex - 1);
  }, [currentIndex, goToSlide]);

  useEffect(() => {
    if (isPaused || reducedMotion || totalSlides <= 1) return;

    const interval = setInterval(goToNext, autoPlayInterval);
    return () => clearInterval(interval);
  }, [isPaused, reducedMotion, goToNext, autoPlayInterval, totalSlides]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        goToNext();
      } else {
        goToPrev();
      }
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    isPointerDown.current = true;
    hasDragged.current = false;
    touchStartX.current = e.clientX;
    touchEndX.current = e.clientX;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isPointerDown.current) return;
    touchEndX.current = e.clientX;
    if (Math.abs(touchStartX.current - touchEndX.current) > 5) {
      hasDragged.current = true;
      if (!isDragging) setIsDragging(true);
    }
  };

  const finishMouseDrag = () => {
    if (!isPointerDown.current) return;
    isPointerDown.current = false;
    setIsDragging(false);
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;
    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        goToNext();
      } else {
        goToPrev();
      }
    }
  };

  const handleLinkClick = (slide: HeroCarouselSlide) => (e: React.MouseEvent) => {
    if (hasDragged.current) {
      e.preventDefault();
      hasDragged.current = false;
      return;
    }
    // Click-count beacon for API-driven banners
    if (slide.id) {
      fetch('/api/view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'banner', id: slide.id }),
        keepalive: true,
      }).catch(() => {});
    }
  };

  if (!slides.length) return null;

  return (
    <div
      className={`${styles.carouselContainer}${isDragging ? ` ${styles.dragging}` : ''}`}
      ref={containerRef}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => {
        setIsPaused(false);
        finishMouseDrag();
      }}
      onFocus={() => setIsPaused(true)}
      onBlur={(e) => {
        if (!containerRef.current?.contains(e.relatedTarget as Node)) setIsPaused(false);
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={finishMouseDrag}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className={styles.carouselTrack} style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
        {slides.map((slide, index) => (
          <Link
            key={slide.id ?? index}
            href={localizeFullPath(slide.url)}
            className={styles.slide}
            onClick={handleLinkClick(slide)}
            onDragStart={(e) => e.preventDefault()}
            aria-hidden={index !== currentIndex}
            tabIndex={index === currentIndex ? undefined : -1}
          >
            {slide.mobileImage ? (
              /* Art-directed <picture>: the browser downloads ONLY the source
                 matching the viewport, so the first slide - the LCP element -
                 can load eagerly with fetchpriority=high on desktop AND
                 mobile. The previous two-hidden-divs approach forced the
                 desktop image to stay lazy (eager/priority ignores
                 display:none and would download the hidden variant too).
                 Plain <img> is equivalent here: the sources were already
                 `unoptimized` local files. Media query must match the
                 .desktopImage/.mobileImage breakpoint history (640px). */
              <picture>
                <source media="(max-width: 640px)" srcSet={slide.mobileImage} />
                <img
                  key={viewportGeneration}
                  src={slide.image}
                  alt={slide.alt || `Hero slide ${index + 1}`}
                  className={styles.slideImage}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  // low on slides 2+ so they never compete with the LCP fetch
                  fetchPriority={index === 0 ? 'high' : 'low'}
                  decoding="async"
                  draggable={false}
                />
              </picture>
            ) : (
              <Image
                src={slide.image}
                alt={slide.alt || `Hero slide ${index + 1}`}
                fill
                sizes="(max-width: 768px) 100vw, 1200px"
                style={{ objectFit: 'cover' }}
                priority={index === 0}
                fetchPriority={index === 0 ? 'high' : 'low'}
                unoptimized
              />
            )}
          </Link>
        ))}
      </div>

      {showDots && totalSlides > 1 && (
        <div className={styles.dotsContainer}>
          {slides.map((_, index) => (
            <button
              key={index}
              className={`${styles.dot}${index === currentIndex ? ` ${styles.active}` : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={t('goToSlide', { number: index + 1 })}
              aria-current={index === currentIndex ? 'true' : undefined}
            >
              <span className={styles.dotVisual} aria-hidden="true" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default HeroCarousel;
